import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, X } from "lucide-react";
import { validateEmail } from "../../../helpers/management/vendors";
import { LoaderSpinner } from "../../../components/LoaderSpinner";

const INPUT_CLASS = "border-input w-full min-w-0 rounded-md border bg-transparent px-3 py-1 shadow-xs transition-[color,box-shadow] outline-none h-8 text-[12px] flex-1 placeholder:text-gray-600 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50";

const VendorSettingsModal = ({
    isOpen,
    onClose,
    onSuccess,
    access = "edit",
}) => {
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadEmails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/v1/metadata/vendor-emails");
            if (!response.ok) throw new Error("Failed to load emails");
            const data = await response.json();
            setEmails(data.emails || []);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load team emails", { position: "bottom-right" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setInputValue("");
            setError("");
            loadEmails();
        }
    }, [isOpen, loadEmails]);

    const addEmail = () => {
        if (access !== "edit") return;

        const email = inputValue.trim().replace(/,$/, "");
        if (!email) return;

        if (!validateEmail(email)) {
            setError("Please enter a valid email address");
            return;
        }

        if (emails.includes(email)) {
            setInputValue("");
            setError("");
            return;
        }

        setEmails((prev) => [...prev, email]);
        setInputValue("");
        setError("");
    };

    const removeEmail = (index) => {
        if (access !== "edit") return;
        setEmails((prev) => prev.filter((_, i) => i !== index));
        setError("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addEmail();
        }
    };

    const handleSave = async () => {
        if (access !== "edit") return;

        if (emails.length === 0) {
            setError("At least one valid email is required");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch("/api/v1/metadata/vendor-emails", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ emails }),
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || "Failed to update emails");
            }

            toast.success("Default emails updated successfully", { position: "bottom-right" });
            onSuccess?.();
            onClose();
        } catch (e) {
            console.error(e);
            toast.error(e.message || "Failed to update emails", { position: "bottom-right" });
        } finally {
            setLoading(false);
        }
    };

    const canAddEmail = inputValue.trim() && validateEmail(inputValue.trim().replace(/,$/, ""));

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
        >
            <button
                type="button"
                className="absolute inset-0 bg-gray-900/40"
                aria-label="Close"
                onClick={onClose}
            />
            <div className="relative z-[81] w-full max-w-[520px] rounded-lg border border-border bg-white p-6 shadow-lg">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex flex-col gap-2 text-left">
                    <h2 className="text-[15px] font-semibold">Supplier Module Settings</h2>
                    <p className="text-[12px] text-gray-600">
                        Manage Kings Loot team emails. These are automatically assigned to every vendor.
                    </p>
                </div>

                <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                        <label className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                            Kings Loot Team Emails
                        </label>

                        {loading && (
                            <div className="flex justify-center items-center py-4 ">
                                <LoaderSpinner width="16" height="16" border="2" colorSpinner="gray" />
                            </div>
                        )}
                        {emails.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {emails.map((email, index) => (
                                    <span
                                        key={`${email}-${index}`}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary text-white text-[11px] font-medium"
                                    >
                                        {email}
                                        {access === "edit" && (
                                            <button
                                                type="button"
                                                onClick={() => removeEmail(index)}
                                                className="ml-0.5 hover:opacity-70 transition-opacity"
                                                aria-label={`Remove ${email}`}
                                            >
                                                <X className="w-2.5 h-2.5" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}

                        {access === "edit" && (
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    value={inputValue}
                                    disabled={loading}
                                    onChange={(e) => {
                                        setInputValue(e.target.value);
                                        if (error) setError("");
                                    }}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Add email and press Enter"
                                    className={INPUT_CLASS}
                                />
                                <button
                                    type="button"
                                    disabled={loading || !canAddEmail}
                                    onClick={addEmail}
                                    className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 border bg-transparent shadow-xs hover:bg-gray-100 rounded-md gap-1.5 h-8 px-2.5 border-border"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {error && (
                            <p className="text-[11px] text-red-500">{error}</p>
                        )}

                        <p className="text-[11px] text-gray-600">
                            Changes will sync to all existing and new vendors automatically.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
                    <button
                        type="button"
                        disabled={loading}
                        onClick={onClose}
                        className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 border bg-transparent shadow-xs hover:bg-gray-100 rounded-md gap-1.5 px-3 h-8 text-[12px] border-border"
                    >
                        Cancel
                    </button>
                    {access === "edit" && (
                        <button
                            type="button"
                            disabled={loading}
                            onClick={handleSave}
                            className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 rounded-md gap-1.5 px-3 h-8 text-[12px] bg-primary text-white hover:bg-gray-600 min-w-[108px]"
                        >
                            {loading ? (
                                <LoaderSpinner width="16" height="16" border="2" colorSpinner="gray" />
                            ) : (
                                "Save Changes"
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorSettingsModal;
